// src/components/desk/FeedPager.jsx — ONE pager for every Desk feed.
//
// Owner, 2026-09-02: "show all page numbes how many pages are there now we only see which page
// we are on". The strip printed „გვერდი 3 / 12" — you knew where you were, and getting anywhere
// else meant the prev/next buttons or typing a number. The numbers are the control now, and the
// LAST one being visible is what answers "how many are there".
//
// Owner, 2026-09-05: "build pager with start end pages and page write in". Two changes:
//   (1) the END page is drawn even while the feed is still loading — IntakeTab passes the pages
//       that EXIST NOW as `loadedPages` and the pages the server total implies as `totalPages`;
//       a page past the loaded ones is drawn muted and titled as such, and clicking it asks
//       for it (the feed's own near-end prefetch then loads until it is reachable). The strip
//       corrects itself as rows arrive, so the promise is honest: "about this many", not a
//       figure that quietly grows.
//   (2) the write-in box is ALWAYS offered (it used to appear only past nine pages).
//
// ⚠️ IT IS A COMPONENT BECAUSE THERE WERE TWO COPIES, and building the numbers into one of them
// proved it. LeadsFeed's pager carried the comment "the property feed's pager, VERBATIM (owner:
// same exact system)" — two hand-kept copies of one control, which is the same disease as the
// Desk's filters needing edits in eleven places. Adding numbers to IntakeTab alone would have
// shipped a Desk where the property feed has clickable pages and the leads feed does not, and
// the owner would have found that in a minute. One component, both callers.
//
// ⚠️ WHAT IT DELIBERATELY DOES NOT OWN: the page state, or the URL. A pager that stored its own
// page could not be restored from a link, and the two feeds keep their page in different places
// (IntakeTab's is URL-backed, LeadsFeed's is local). It takes a number and reports a number.
import { pageWindow, GAP } from '../../lib/pageWindow.js'
import { t, tLog } from '../../lib/i18n.js'
import { btn } from './primitives.jsx'

export function FeedPager({ page, totalPages, onPage, morePending = false, showJump = false, loadedPages = null }) {
  if (!(totalPages > 1)) return null
  const loaded = loadedPages == null ? totalPages : Math.max(1, Math.min(loadedPages, totalPages))
  // The current page can only be one that exists; a target past the loaded pages is the
  // caller's state (it keeps asking the feed to load), so the strip highlights the page shown.
  const cur = Math.min(Math.max(0, page || 0), loaded - 1)
  const estimated = loaded < totalPages
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
      <button type="button" className={btn('ghost')} disabled={cur === 0}
        onClick={() => onPage(Math.max(0, cur - 1))}>{t('desk_pagination_prev')}</button>

      <div className="flex items-center gap-0.5">
        {pageWindow(cur, totalPages).map((n, i) => n === GAP ? (
          // Not a button: a gap is a statement that pages were skipped, not somewhere to go.
          <span key={`gap${i}`} className="px-1 text-xs text-muted-foreground/50 select-none">{GAP}</span>
        ) : (
          <button key={n} type="button" data-page={n}
            aria-current={n === cur ? 'page' : undefined}
            aria-label={tLog('desk_pagination_go_to', { n: n + 1 })}
            title={n >= loaded ? t('desk_pagination_estimated_page') : undefined}
            className={`min-w-[1.75rem] rounded-md px-1.5 py-1 text-xs tabular-nums transition-colors ${
              n === cur
                ? 'bg-primary text-primary-foreground font-semibold'
                : n >= loaded
                  ? 'text-muted-foreground/60 border border-dashed border-border hover:bg-accent hover:text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}
            onClick={() => onPage(n)}>
            {n + 1}
          </button>
        ))}
        {/* The honest half of B653: more results are still being fetched, so the last number is
            a FLOOR, not a final figure. Without it the count grows silently and reads as a bug.
            It sits after the last page, beside the number it qualifies. Not drawn when the
            caller already draws the estimated end page — then the dashed pages say it. */}
        {morePending && !estimated && (
          <span className="px-1 text-xs text-muted-foreground" title={t('desk_pagination_more_pending')}>+</span>
        )}
      </div>

      <button type="button" className={btn('ghost')} disabled={cur >= totalPages - 1}
        onClick={() => onPage(Math.min(totalPages - 1, cur + 1))}>{t('desk_pagination_next')}</button>

      {/* Typing a page beats clicking through the strip (owner, earlier: "allow user to pick page
          by putting in value themselves"; 2026-09-05: "page write in" — always offered now).
          ⚠️ Commits on Enter and on blur, never per keystroke: typing "12" would otherwise
          navigate through page 1 on the way. */}
      {showJump && (
        <input type="number" min={1} max={totalPages} defaultValue={cur + 1}
          key={`pg-${cur}-${totalPages}`}
          aria-label={t('desk_pagination_jump')} title={t('desk_pagination_jump')}
          className="w-14 rounded-md border border-input bg-background px-1.5 py-1 text-xs text-center tabular-nums"
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
          onBlur={e => {
            const n = Math.round(Number(e.currentTarget.value))
            if (!Number.isFinite(n) || n < 1) { e.currentTarget.value = String(cur + 1); return }
            onPage(Math.min(totalPages - 1, n - 1))
          }} />
      )}
    </div>
  )
}
